/**
 * 4-step stepper for the onboarding flow — refreshed 2026-05-22 redesign.
 *
 * Numbered coral circles + single muted connector line behind them.
 *   - Past: solid coral circle + white number, muted clickable label
 *   - Current: solid coral circle + white number + ring halo, bold dark
 *   - Future: white circle with slate outline + muted number, muted label
 *
 * Step order (flipped 2026-05-22): Business → Website → Connect → Go live.
 *
 * See:
 *   - STATUS/PLAN_ONBOARDING_REDESIGN_PORT.md
 *   - heymotek-web/STATUS/PLAN_ONBOARDING_REDESIGN.md
 *   - Live preview source: https://heymotek.com/preview/onboarding/
 */

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface Props {
  current: 1 | 2 | 3 | 4;
}

const STEPS = [
  { num: 1, label: 'Business', href: '/onboarding' },
  { num: 2, label: 'Website', href: '/onboarding/website' },
  { num: 3, label: 'Connect', href: '/onboarding/connect' },
  { num: 4, label: 'Go live', href: '/onboarding/go-live' },
] as const;

export function OnboardingStepper({ current }: Props) {
  return (
    <nav aria-label="Onboarding progress" className="w-full">
      <ol className="relative flex items-start">
        {/* Connector line — runs through the circles' vertical center.
            12.5% / 87.5% align to the centers of the first + last circles
            in a 4-column equal-width flex row. */}
        <div className="absolute top-[9px] left-[12.5%] right-[12.5%] h-0.5 bg-dark/10 z-0" />

        {STEPS.map((step) => {
          const isPast = step.num < current;
          const isCurrent = step.num === current;

          if (isPast) {
            return (
              <li
                key={step.num}
                className="flex-1 flex flex-col items-center gap-2 relative z-10 min-w-0"
              >
                <Link
                  to={step.href}
                  className="group flex flex-col items-center gap-2 min-w-0"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-white text-[10px] font-bold">
                    {step.num}
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-muted-foreground group-hover:text-dark group-hover:underline truncate">
                    {step.label}
                  </span>
                </Link>
              </li>
            );
          }

          if (isCurrent) {
            return (
              <li
                key={step.num}
                className="flex-1 flex flex-col items-center gap-2 relative z-10 min-w-0"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-white text-[10px] font-bold ring-4 ring-brand/20">
                  {step.num}
                </span>
                <span className="text-xs sm:text-sm font-bold text-dark truncate">
                  {step.label}
                </span>
              </li>
            );
          }

          // Future
          return (
            <li
              key={step.num}
              className="flex-1 flex flex-col items-center gap-2 relative z-10 min-w-0"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white border-2 border-dark/15 text-muted-foreground text-[10px] font-bold">
                {step.num}
              </span>
              <span className={cn('text-xs sm:text-sm font-medium text-muted-foreground truncate')}>
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
